import { useState } from 'react';
import { IslemDagilim, OdemeOzet } from '../utils/raporHesapla';

interface Props {
  dagilim: IslemDagilim[];
  odemeler: OdemeOzet[];
  toplamServis: number;
}

function DonutChart({ dagilim, toplam }: { dagilim: IslemDagilim[]; toplam: number }) {
  const [hovered, setHovered] = useState<string | null>(null);
  const R = 70;
  const cx = 100;
  const cy = 100;
  const circumference = 2 * Math.PI * R;
  const gap = 3;

  let cumulative = 0;
  const slices = dagilim.map(d => {
    const pct = d.sayi / toplam;
    const dashLen = pct * circumference - gap;
    const offset = circumference - cumulative * circumference;
    cumulative += pct;
    return { ...d, dashLen, offset };
  });

  const hoveredItem = hovered ? dagilim.find(d => d.islem === hovered) : null;

  return (
    <div className="flex items-center gap-6">
      <div className="relative flex-shrink-0">
        <svg width="200" height="200" viewBox="0 0 200 200">
          {/* Arka plan halka */}
          <circle cx={cx} cy={cy} r={R} fill="none" stroke="#f1f5f9" strokeWidth="22" />
          {slices.map(s => (
            <circle
              key={s.islem}
              cx={cx} cy={cy} r={R}
              fill="none"
              stroke={s.renk}
              strokeWidth={hovered === s.islem ? 26 : 22}
              strokeDasharray={`${Math.max(s.dashLen, 0)} ${circumference}`}
              strokeDashoffset={s.offset}
              strokeLinecap="round"
              transform={`rotate(-90 ${cx} ${cy})`}
              style={{ transition: 'stroke-width 0.2s, opacity 0.2s', opacity: hovered && hovered !== s.islem ? 0.4 : 1, cursor: 'pointer' }}
              onMouseEnter={() => setHovered(s.islem)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
          {/* Ortadaki metin */}
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize="24" fontWeight="700" fill="#1e293b">
            {hoveredItem ? hoveredItem.sayi : toplam}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" fontSize="11" fill="#94a3b8">
            {hoveredItem ? hoveredItem.islem.split(' ')[0] : 'toplam'}
          </text>
          {hoveredItem && (
            <text x={cx} y={cy + 30} textAnchor="middle" fontSize="11" fontWeight="600" fill={hoveredItem.renk}>
              %{hoveredItem.yuzde}
            </text>
          )}
        </svg>
      </div>

      {/* Açıklama */}
      <div className="flex-1 space-y-3">
        {dagilim.map(d => (
          <div
            key={d.islem}
            className="cursor-pointer"
            onMouseEnter={() => setHovered(d.islem)}
            onMouseLeave={() => setHovered(null)}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.renk }}></span>
                <span className={`text-xs font-medium transition-colors ${hovered === d.islem ? 'text-gray-900 font-bold' : 'text-gray-600'}`}>{d.islem}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-gray-700">{d.sayi}</span>
                <span className="text-xs text-gray-400 w-8 text-right">%{d.yuzde}</span>
              </div>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${d.yuzde}%`, backgroundColor: d.renk, opacity: hovered && hovered !== d.islem ? 0.3 : 1 }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function IslemDagilimiKart({ dagilim, odemeler, toplamServis }: Props) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Donut */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="mb-4">
          <h3 className="text-sm font-bold text-gray-800">İşlem Türü Dağılımı</h3>
          <p className="text-xs text-gray-400 mt-0.5">Tüm servisler arasında — üstüne gelin</p>
        </div>
        <DonutChart dagilim={dagilim} toplam={toplamServis} />
      </div>

      {/* Ödeme tipi */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="mb-4">
          <h3 className="text-sm font-bold text-gray-800">Ödeme Tipi Dağılımı</h3>
          <p className="text-xs text-gray-400 mt-0.5">Nakit / Kart / Diğer oranları</p>
        </div>
        <div className="space-y-4 mt-2">
          {odemeler.map((o, i) => {
            const renkler = ['#0ea5e9', '#10b981', '#f59e0b'];
            const renk = renkler[i % renkler.length];
            const yuzde = Math.round((o.sayi / toplamServis) * 100);
            return (
              <div key={o.tip}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: renk }}></span>
                    <span className="text-sm font-semibold text-gray-700">{o.tip}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-gray-800">₺{o.toplam.toLocaleString('tr-TR')}</span>
                    <span className="text-xs text-gray-400 ml-2">{o.sayi} servis</span>
                  </div>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${yuzde}%`, backgroundColor: renk }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1 text-right">%{yuzde}</p>
              </div>
            );
          })}
        </div>

        {/* Toplam kutusu */}
        <div className="mt-5 bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-gray-600 font-medium">Toplam Gelir</span>
          <span className="text-lg font-bold text-emerald-600">
            ₺{odemeler.reduce((s, o) => s + o.toplam, 0).toLocaleString('tr-TR')}
          </span>
        </div>
      </div>
    </div>
  );
}
